import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { posts, techNews } from '../data';
import PostCard from '../components/PostCard';
import TechNewsCard from '../components/TechNewsCard';
import { ArrowRight, Sparkles, BookOpen, Newspaper } from 'lucide-react';

export default function Home() {
  const latestPosts = posts.slice(0, 3);
  const latestNews = techNews.slice(0, 3);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background Effects - 粉紫色渐变 */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 left-1/4 w-[500px] h-[500px] bg-pink-500/25 rounded-full blur-3xl animate-pulse-slow" />
          <div className="absolute -bottom-20 right-1/4 w-[500px] h-[500px] bg-purple-500/25 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-blue-400/15 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-6 whitespace-nowrap">
              <span className="gradient-text">探索技术的边界</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-pink-200/80 max-w-3xl mx-auto mb-8 px-4">
              分享技术学习笔记，追踪前沿科技动态
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/posts" className="btn-primary flex items-center">
                <BookOpen size={18} className="mr-2" />
                浏览文章
              </Link>
              <Link to="/tech-news" className="btn-secondary flex items-center">
                <Newspaper size={18} className="mr-2" />
                科技资讯
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Latest Posts Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="flex items-center justify-between mb-12"
          >
            <div className="flex items-center space-x-2 sm:space-x-3">
              <Sparkles className="text-pink-400 w-5 h-5 sm:w-6 sm:h-6" />
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">最新文章</h2>
            </div>
            <Link
              to="/posts"
              className="flex items-center text-pink-400 hover:text-pink-300 transition-colors"
            >
              查看全部 <ArrowRight size={18} className="ml-1" />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {latestPosts.map((post, index) => (
              <PostCard key={post.id} post={post} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Tech News Preview Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-dark-800/50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="flex items-center justify-between mb-12"
          >
            <div className="flex items-center space-x-2 sm:space-x-3">
              <Newspaper className="text-purple-400 w-5 h-5 sm:w-6 sm:h-6" />
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">科技资讯</h2>
            </div>
            <Link
              to="/tech-news"
              className="flex items-center text-pink-400 hover:text-pink-300 transition-colors"
            >
              查看全部 <ArrowRight size={18} className="ml-1" />
            </Link>
          </motion.div>

          <div className="space-y-4">
            {latestNews.map((category) =>
              category.items.slice(0, 1).map((item, index) => (
                <TechNewsCard key={item.title} item={item} index={index} />
              ))
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-8 md:p-12 text-center"
          >
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
              想了解更多？
            </h2>
            <p className="text-pink-200/70 mb-8 max-w-2xl mx-auto">
              访问关于页面了解更多关于我和这个博客的信息
            </p>
            <Link to="/about" className="btn-primary inline-flex items-center">
              关于我 <ArrowRight size={18} className="ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
